import { ComponentType, useMemo } from 'react';

import { FeatureFlaggedProps } from '@backstage/core-app-api';

import { useFlag } from '@spotify-confidence/react';
import { Value } from '@spotify-confidence/sdk';

import { useLocalOverride } from './overrideStore';

export const useFeatureFlag = <T extends Value>(
  flag: string,
  defaultValue: T,
): T => useLocalOverride(flag, useFlag(flag, defaultValue));

export const FeatureFlagged = (props: FeatureFlaggedProps) => {
  const flag = 'with' in props ? props.with : props.without;
  const desired = 'with' in props;
  const { enabled } = useFeatureFlag(flag, { enabled: false });
  return <>{enabled === desired ? props.children : null}</>;
};

export const splitComponent = <
  V extends Value.Struct,
  P extends Record<string, unknown> = Record<string, unknown>,
>(
  flag: string,
  defaultValue: V,
  factory: (value: V) => ComponentType<P>,
) => {
  const SplitComponent = (props: P) => {
    const value = useFeatureFlag(flag, defaultValue);
    const Component = useMemo(
      () => (value ? factory(value as V) : null),
      [value],
    );
    if (!Component) {
      return null;
    }

    return <Component {...props} />;
  };
  SplitComponent.displayName = `splitComponent(${flag})`;
  return SplitComponent;
};
